import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core, Decorative } from "@faire/design-tokens";
import { getSessionCountry } from "@faire/retailer-visitor-shared/serialized-data/getSessionCountry";
import { getSettingEnableNewNAMCommissionModelWebRetailer } from "@faire/retailer-visitor-shared/settings/getSettingEnableNewNAMCommissionModelWebRetailer";
import { getSettingUpdatedNAMCommissionAnnouncement } from "@faire/retailer-visitor-shared/settings/getSettingUpdatedNAMCommissionAnnouncement";
import { COMMISSION_NAM_UPDATE_BLOG_POST_LINK } from "@faire/retailer-visitor-shared/views/brand/consts";
import { LinkFormatting } from "@faire/retailer-visitor-shared/views/brand/styled";
import { ISO_TO_COUNTRY_NAME_WITHOUT_SANCTIONS } from "@faire/web--source/common/consts/ISO_TO_COUNTRY_NAME";
import { THREE_LETTER_EUROPE_SUPPORTED_COUNTRIES } from "@faire/web--source/common/consts/THREE_LETTER_EUROPE_SUPPORTED_COUNTRIES";
import { THREE_LETTER_PRIMARY_COUNTRY_CODES } from "@faire/web--source/common/consts/THREE_LETTER_PRIMARY_COUNTRY_CODES";
import { MediumFormatting } from "@faire/web--source/common/localization/formatting";
import { DEPRECATED_backwardsCompatibleValues } from "@faire/web--source/common/localization/localize";
import { formatMoney } from "@faire/web--source/common/money";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { AlertBanner } from "@faire/web--source/ui/AlertBanner";
import { EmbeddedLink } from "@faire/web--source/ui/EmbeddedLink";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { IFaireMoney } from "@faire/web-api--source/indigofair/data/IFaireMoney";
import { route as brandsPricingRoute } from "@faire/web-api--source/routes/brands/pricing";
import supportArticle from "@faire/web-api--source/routes/support/articles/articleId";
import supportTopic from "@faire/web-api--source/routes/support/topics/topicId";
import React from "react";
var faqCommissionStructure = function(strictLocalize) {
    return {
        question: strictLocalize({
            id: "AznxLO",
            defaultMessage: [
                {
                    type: 0,
                    value: "What is Faire\u2019s commission structure?"
                }
            ]
        }),
        answer: /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "/ibF3u",
            defaultMessage: [
                {
                    type: 0,
                    value: "You won't pay commission when you use Faire with your existing customers. Brands only pay commission on new accounts who find them through our marketplace\u201425% on opening orders and 15% on reorders."
                }
            ]
        }),
        eventKey: "commission-structure"
    };
};
var faqGetPaid = function(strictLocalize) {
    return {
        question: strictLocalize({
            id: "RW0tgm",
            defaultMessage: [
                {
                    type: 0,
                    value: "How and when do I get paid?"
                }
            ]
        }),
        answer: /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "ZJCNsF",
            defaultMessage: [
                {
                    type: 0,
                    value: "If you\u2019re based in a country we support, we\u2019ll pay all your orders directly to your bank account in your local currency. You will be able to set your preferred payment method when you join Faire. We guarantee payout 30 days after an order is shipped, or you can choose to get next-day payment for a 3% fee. "
                },
                {
                    type: 8,
                    value: "HelpCenterArticleLink",
                    children: [
                        {
                            type: 0,
                            value: "Learn more"
                        }
                    ]
                }
            ],
            values: DEPRECATED_backwardsCompatibleValues({
                HelpCenterArticleLink: function(chunk) {
                    return /*#__PURE__*/ _jsx(EmbeddedLink, {
                        linkTo: supportTopic("360001460712"),
                        children: /*#__PURE__*/ _jsx(Typography, {
                            variant: {
                                tabletAndAbove: "subheadingSansRegular",
                                mobileAndAbove: "paragraphSansRegular"
                            },
                            color: Core.text.subdued,
                            children: chunk
                        })
                    });
                }
            })
        }),
        eventKey: "get-paid"
    };
};
var faqUseReps = function(strictLocalize) {
    return {
        question: strictLocalize({
            id: "a/cfnT",
            defaultMessage: [
                {
                    type: 0,
                    value: "Can I use Faire if I already have reps?"
                }
            ]
        }),
        answer: /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "TUe37c",
            defaultMessage: [
                {
                    type: 0,
                    value: "Yes! We work seamlessly alongside your team, and you can choose to block regions where you already have representation. Plus, we guarantee you 0% commission on all orders from existing accounts you and your reps have already gotten."
                }
            ]
        }),
        eventKey: "use-representation"
    };
};
export var getAvailableCountryMessage = function(strictLocalize) {
    var userCountry = getSessionCountry();
    if (userCountry === THREE_LETTER_PRIMARY_COUNTRY_CODES.CAN) {
        return strictLocalize({
            id: "YnHIIz",
            defaultMessage: [
                {
                    type: 0,
                    value: "Faire is currently live in Canada, U.S., UK, France, Italy, Germany, Spain, and 10 other countries in Europe."
                }
            ]
        });
    } else if (userCountry === THREE_LETTER_PRIMARY_COUNTRY_CODES.USA) {
        return strictLocalize({
            id: "VZX1O9",
            defaultMessage: [
                {
                    type: 0,
                    value: "Faire is currently live in the U.S., Canada, UK, France, Italy, Germany, Spain, and 10 other countries in Europe."
                }
            ]
        });
    } else if (userCountry === THREE_LETTER_PRIMARY_COUNTRY_CODES.GBR) {
        return strictLocalize({
            id: "pNMA9u",
            defaultMessage: [
                {
                    type: 0,
                    value: "Faire is currently live in the UK, U.S., Canada, and most of the E.U."
                }
            ]
        });
    } else if (THREE_LETTER_EUROPE_SUPPORTED_COUNTRIES.includes(userCountry)) {
        return strictLocalize({
            id: "cascY6",
            defaultMessage: [
                {
                    type: 0,
                    value: "Faire is currently live in "
                },
                {
                    type: 1,
                    value: "country"
                },
                {
                    type: 0,
                    value: " and most of the E.U., and in the UK, U.S., and Canada."
                }
            ]
        }, DEPRECATED_backwardsCompatibleValues({
            country: // @ts-expect-error FIXME(implicitAny): https://faire.link/no-implicit-any
            ISO_TO_COUNTRY_NAME_WITHOUT_SANCTIONS(strictLocalize)[userCountry]
        }));
    }
    return strictLocalize({
        id: "fpYmTT",
        defaultMessage: [
            {
                type: 0,
                value: "Unfortunately, Faire isn't available in "
            },
            {
                type: 1,
                value: "country"
            },
            {
                type: 0,
                value: " yet. We are currently live in the U.S., Canada, UK, France, Italy, Germany, Spain, and 10 other countries in Europe."
            }
        ]
    }, DEPRECATED_backwardsCompatibleValues({
        country: // @ts-expect-error FIXME(implicitAny): https://faire.link/no-implicit-any
        ISO_TO_COUNTRY_NAME_WITHOUT_SANCTIONS(strictLocalize)[userCountry]
    }));
};
var faqAvailableCountries = function(strictLocalize) {
    return {
        question: strictLocalize({
            id: "232mgb",
            defaultMessage: [
                {
                    type: 0,
                    value: "Which countries are currently supported on Faire?"
                }
            ]
        }),
        answer: /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "TMVGoG",
            defaultMessage: [
                {
                    type: 0,
                    value: "Faire is available in the U.S., Canada, the UK, and most countries in the EU. For a full list of countries, visit "
                },
                {
                    type: 8,
                    value: "SupportedCountriesArticleLink",
                    children: [
                        {
                            type: 0,
                            value: "Where is Faire available?"
                        }
                    ]
                }
            ],
            values: DEPRECATED_backwardsCompatibleValues({
                SupportedCountriesArticleLink: function(chunk) {
                    return /*#__PURE__*/ _jsx(EmbeddedLink, {
                        linkTo: supportArticle("360016111311"),
                        children: /*#__PURE__*/ _jsx(Typography, {
                            variant: {
                                tabletAndAbove: "subheadingSansRegular",
                                mobileAndAbove: "paragraphSansRegular"
                            },
                            color: Core.text.subdued,
                            children: chunk
                        })
                    });
                }
            })
        }),
        eventKey: "available-countries"
    };
};
export var ACCORDION_ITEMS = function(strictLocalize) {
    return [
        faqCommissionStructure(strictLocalize),
        {
            question: strictLocalize({
                id: "sk1Tqx",
                defaultMessage: [
                    {
                        type: 0,
                        value: "How do order fulfillment and shipping work?"
                    }
                ]
            }),
            answer: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                id: "nBn9VM",
                defaultMessage: [
                    {
                        type: 0,
                        value: "We have negotiated shipping rates, so all you have to do is print off pre-paid shipping labels, right from your portal. Or, if you prefer, you can ship on your own and we\u2019ll reimburse you for the shipping costs."
                    }
                ]
            }),
            eventKey: "fulfillment-and-shipping-v2"
        },
        faqGetPaid(strictLocalize),
        faqUseReps(strictLocalize),
        faqAvailableCountries(strictLocalize)
    ];
};
var newCustomerFeeMoney = function() {
    return formatMoney({
        money: IFaireMoney.build({
            amount_cents: 1000
        }),
        omitCentsIfZero: true,
        compact: true
    });
};
var commissionUpdateNAMCommissionStructure = function(strictLocalize) {
    return {
        question: strictLocalize({
            id: "lqS53Y",
            defaultMessage: [
                {
                    type: 0,
                    value: "How much does it cost to sell on Faire?"
                }
            ]
        }),
        answer: getSettingEnableNewNAMCommissionModelWebRetailer() ? /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    variant: "paragraphSansRegular",
                    children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "cO/Mf/",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "The total cost of selling on Faire is comprised of commission and payment processing fees that are applied to the order total. View more details on our "
                            },
                            {
                                type: 8,
                                value: "LinkFormatting",
                                children: [
                                    {
                                        type: 0,
                                        value: "Pricing page"
                                    }
                                ]
                            },
                            {
                                type: 0,
                                value: "."
                            }
                        ],
                        values: {
                            LinkFormatting: function(chunks) {
                                return LinkFormatting(chunks, brandsPricingRoute);
                            }
                        }
                    })
                }),
                /*#__PURE__*/ _jsx(Spacer, {
                    height: "2x"
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    variant: "paragraphSansRegular",
                    children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "evlLIe",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Your commission on an order is based on how the customer ordered from your Faire shop."
                            }
                        ]
                    })
                }),
                /*#__PURE__*/ _jsxs(Typography, {
                    variant: "paragraphSansRegular",
                    children: [
                        "\xa0\xa0•\xa0",
                        /*#__PURE__*/ _jsx(StrictLocalMsg, {
                            id: "5owuY9",
                            defaultMessage: [
                                {
                                    type: 0,
                                    value: "For customers who discover you through Faire\u2019s marketplace, you\u2019ll pay 15% commission on all orders and an additional "
                                },
                                {
                                    type: 1,
                                    value: "newCustomerFeeMoney"
                                },
                                {
                                    type: 0,
                                    value: " new customer fee on opening orders."
                                }
                            ],
                            values: {
                                newCustomerFeeMoney: newCustomerFeeMoney()
                            }
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(Typography, {
                    variant: "paragraphSansRegular",
                    children: [
                        "\xa0\xa0•\xa0",
                        /*#__PURE__*/ _jsx(StrictLocalMsg, {
                            id: "rbG2UH",
                            defaultMessage: [
                                {
                                    type: 0,
                                    value: "For any existing or prospective customers who order through your Faire Direct link, you\u2019ll pay 0% commission."
                                }
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Spacer, {
                    height: "2x"
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    variant: "paragraphSansRegular",
                    children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "NVGXSu",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "A "
                            },
                            {
                                type: 8,
                                value: "MediumFormatting",
                                children: [
                                    {
                                        type: 0,
                                        value: "payment processing"
                                    }
                                ]
                            },
                            {
                                type: 0,
                                value: " fee will also be applied to all your orders. The payment processing fee you pay is based on your selected payout speed."
                            }
                        ],
                        values: {
                            MediumFormatting: MediumFormatting
                        }
                    })
                })
            ]
        }) : /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    variant: "paragraphSansRegular",
                    children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "/ibF3u",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "You won't pay commission when you use Faire with your existing customers. Brands only pay commission on new accounts who find them through our marketplace\u201425% on opening orders and 15% on reorders."
                            }
                        ]
                    })
                }),
                getSettingUpdatedNAMCommissionAnnouncement() && !getSettingEnableNewNAMCommissionModelWebRetailer() ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(Spacer, {
                            height: "2x"
                        }),
                        /*#__PURE__*/ _jsx(AlertBanner, {
                            message: /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraphSansRegular",
                                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                    id: "8yCzl8",
                                    defaultMessage: [
                                        {
                                            type: 0,
                                            value: "On July 5, 2023, we'll be updating commission rates and introducing a payment processing fee in the U.S. and Canada. "
                                        },
                                        {
                                            type: 8,
                                            value: "LinkFormatting",
                                            children: [
                                                {
                                                    type: 0,
                                                    value: "Learn more"
                                                }
                                            ]
                                        }
                                    ],
                                    values: {
                                        LinkFormatting: function(chunks) {
                                            return LinkFormatting(chunks, COMMISSION_NAM_UPDATE_BLOG_POST_LINK);
                                        }
                                    }
                                })
                            }),
                            color: Decorative.tint.neutral
                        })
                    ]
                }) : null
            ]
        }),
        eventKey: "commission-structure"
    };
};
var commissionUpdateNAMOrderFulfillmentAndShipping = function(strictLocalize) {
    return {
        question: strictLocalize({
            id: "sk1Tqx",
            defaultMessage: [
                {
                    type: 0,
                    value: "How do order fulfillment and shipping work?"
                }
            ]
        }),
        answer: /*#__PURE__*/ _jsx(Typography, {
            variant: "paragraphSansRegular",
            children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                id: "nBn9VM",
                defaultMessage: [
                    {
                        type: 0,
                        value: "We have negotiated shipping rates, so all you have to do is print off pre-paid shipping labels, right from your portal. Or, if you prefer, you can ship on your own and we\u2019ll reimburse you for the shipping costs."
                    }
                ]
            })
        }),
        eventKey: "fulfillment-and-shipping-v2"
    };
};
var paymentProcessingFlatFeeMoney = function() {
    return formatMoney({
        money: IFaireMoney.build({
            amount_cents: 30
        }),
        compact: true
    });
};
var commissionUpdateNAMGetPaid = function(strictLocalize) {
    return {
        question: strictLocalize({
            id: "RW0tgm",
            defaultMessage: [
                {
                    type: 0,
                    value: "How and when do I get paid?"
                }
            ]
        }),
        answer: getSettingEnableNewNAMCommissionModelWebRetailer() ? /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    variant: "paragraphSansRegular",
                    children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "2ZPHzH",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "When you start getting orders, you\u2019ll set up the payment account and also select your payout speed\u2014how fast you want to get paid out after you ship an order. Your selected payout speed determines your payment processing fee. You\u2019ll pay more to get paid faster and less to get paid slower."
                            }
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(Spacer, {
                    height: "2x"
                }),
                /*#__PURE__*/ _jsxs(Typography, {
                    variant: "paragraphSansRegular",
                    children: [
                        "\xa0\xa0•\xa0",
                        /*#__PURE__*/ _jsx(StrictLocalMsg, {
                            id: "TPJFez",
                            defaultMessage: [
                                {
                                    type: 8,
                                    value: "MediumFormatting",
                                    children: [
                                        {
                                            type: 0,
                                            value: "Next-day payout:"
                                        }
                                    ]
                                },
                                {
                                    type: 0,
                                    value: " 3.5% + "
                                },
                                {
                                    type: 1,
                                    value: "paymentProcessingFlatFeeMoney"
                                }
                            ],
                            values: {
                                paymentProcessingFlatFeeMoney: paymentProcessingFlatFeeMoney(),
                                MediumFormatting: MediumFormatting
                            }
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(Typography, {
                    variant: "paragraphSansRegular",
                    children: [
                        "\xa0\xa0•\xa0",
                        /*#__PURE__*/ _jsx(StrictLocalMsg, {
                            id: "VoWYiJ",
                            defaultMessage: [
                                {
                                    type: 8,
                                    value: "MediumFormatting",
                                    children: [
                                        {
                                            type: 0,
                                            value: "30-day payout:"
                                        }
                                    ]
                                },
                                {
                                    type: 0,
                                    value: " 2.4% + "
                                },
                                {
                                    type: 1,
                                    value: "paymentProcessingFlatFeeMoney"
                                }
                            ],
                            values: {
                                paymentProcessingFlatFeeMoney: paymentProcessingFlatFeeMoney(),
                                MediumFormatting: MediumFormatting
                            }
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(Typography, {
                    variant: "paragraphSansRegular",
                    children: [
                        "\xa0\xa0•\xa0",
                        /*#__PURE__*/ _jsx(StrictLocalMsg, {
                            id: "S3LG8U",
                            defaultMessage: [
                                {
                                    type: 8,
                                    value: "MediumFormatting",
                                    children: [
                                        {
                                            type: 0,
                                            value: "60-day payout:"
                                        }
                                    ]
                                },
                                {
                                    type: 0,
                                    value: " 1.9% + "
                                },
                                {
                                    type: 1,
                                    value: "paymentProcessingFlatFeeMoney"
                                }
                            ],
                            values: {
                                paymentProcessingFlatFeeMoney: paymentProcessingFlatFeeMoney(),
                                MediumFormatting: MediumFormatting
                            }
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Spacer, {
                    height: "2x"
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    variant: "paragraphSansRegular",
                    children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "0nn+ai",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "After you ship your orders, we\u2019ll deposit your payout in your account based on your selected payout speed. Note that your financial institution may need additional time to process your funds after they are deposited into your account."
                            }
                        ]
                    })
                })
            ]
        }) : /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    variant: "paragraphSansRegular",
                    children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "ZJCNsF",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "If you\u2019re based in a country we support, we\u2019ll pay all your orders directly to your bank account in your local currency. You will be able to set your preferred payment method when you join Faire. We guarantee payout 30 days after an order is shipped, or you can choose to get next-day payment for a 3% fee. "
                            },
                            {
                                type: 8,
                                value: "HelpCenterArticleLink",
                                children: [
                                    {
                                        type: 0,
                                        value: "Learn more"
                                    }
                                ]
                            }
                        ],
                        values: DEPRECATED_backwardsCompatibleValues({
                            HelpCenterArticleLink: function(chunk) {
                                return /*#__PURE__*/ _jsx(EmbeddedLink, {
                                    linkTo: supportTopic("360001460712"),
                                    children: chunk
                                });
                            }
                        })
                    })
                }),
                getSettingUpdatedNAMCommissionAnnouncement() && !getSettingEnableNewNAMCommissionModelWebRetailer() ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(Spacer, {
                            height: "2x"
                        }),
                        /*#__PURE__*/ _jsx(AlertBanner, {
                            message: /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraphSansRegular",
                                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                    id: "8yCzl8",
                                    defaultMessage: [
                                        {
                                            type: 0,
                                            value: "On July 5, 2023, we'll be updating commission rates and introducing a payment processing fee in the U.S. and Canada. "
                                        },
                                        {
                                            type: 8,
                                            value: "LinkFormatting",
                                            children: [
                                                {
                                                    type: 0,
                                                    value: "Learn more"
                                                }
                                            ]
                                        }
                                    ],
                                    values: {
                                        LinkFormatting: function(chunks) {
                                            return LinkFormatting(chunks, COMMISSION_NAM_UPDATE_BLOG_POST_LINK);
                                        }
                                    }
                                })
                            }),
                            color: Decorative.tint.neutral
                        })
                    ]
                }) : null
            ]
        }),
        eventKey: "get-paid"
    };
};
var commissionUpdateNAMUseReps = function(strictLocalize) {
    return {
        question: strictLocalize({
            id: "a/cfnT",
            defaultMessage: [
                {
                    type: 0,
                    value: "Can I use Faire if I already have reps?"
                }
            ]
        }),
        answer: /*#__PURE__*/ _jsx(Typography, {
            variant: "paragraphSansRegular",
            children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                id: "TUe37c",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Yes! We work seamlessly alongside your team, and you can choose to block regions where you already have representation. Plus, we guarantee you 0% commission on all orders from existing accounts you and your reps have already gotten."
                    }
                ]
            })
        }),
        eventKey: "use-representation"
    };
};
var commissionUpdateNAMAvailableCountries = function(strictLocalize) {
    return {
        question: strictLocalize({
            id: "232mgb",
            defaultMessage: [
                {
                    type: 0,
                    value: "Which countries are currently supported on Faire?"
                }
            ]
        }),
        answer: /*#__PURE__*/ _jsx(Typography, {
            variant: "paragraphSansRegular",
            children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                id: "TMVGoG",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Faire is available in the U.S., Canada, the UK, and most countries in the EU. For a full list of countries, visit "
                    },
                    {
                        type: 8,
                        value: "SupportedCountriesArticleLink",
                        children: [
                            {
                                type: 0,
                                value: "Where is Faire available?"
                            }
                        ]
                    }
                ],
                values: DEPRECATED_backwardsCompatibleValues({
                    SupportedCountriesArticleLink: function(chunk) {
                        return /*#__PURE__*/ _jsx(EmbeddedLink, {
                            linkTo: supportArticle("360016111311"),
                            children: chunk
                        });
                    }
                })
            })
        }),
        eventKey: "available-countries"
    };
};
export var commissionUpdateNAMAccordionItems = function(strictLocalize) {
    return [
        commissionUpdateNAMCommissionStructure(strictLocalize),
        commissionUpdateNAMOrderFulfillmentAndShipping(strictLocalize),
        commissionUpdateNAMGetPaid(strictLocalize),
        commissionUpdateNAMUseReps(strictLocalize),
        commissionUpdateNAMAvailableCountries(strictLocalize)
    ];
};
